import React, { useContext, useEffect, useReducer, useState } from 'react';

import { isDesktop, isMobile } from 'react-device-detect';
import { isEmail } from 'react-multi-email';
import FeatherIcon from 'feather-icons-react';
import 'react-multi-email/style.css';
import Notification from 'components/notification';
import { getDomain } from 'constants/utilities/emails';
import { Team, UserTeam } from 'contexts/teams/types';
import { SessionContext } from 'contexts/session';
import { isTeamScopedManager } from 'contexts/session/utilities';
import { sendAccountOwnerInvites, sendUserInvites } from 'services/userInvites';
import { AccountInvite } from '..';
import AddMessage from '../AddMessage';
import AdjustUsersSettings from '../AdjustUsersSettings';
import EmailInput from '../EmailInput';
import Footer from '../Footer';
import RemoveUsers from '../RemoveUsers/RemoveUsers';
import { AdByemailContainer, BodyTitle, LicenseAvailables } from '../styles';
import TeamSelect from '../TeamSelect';
import InputMultiEmails from 'components/inputMultiEmails';
import { SELECT_USERS_COPY } from 'constants/invites';
import {
  defaultInviteMessage,
  userFullName,
} from 'pages/Teams/components/TeamInviteWizard/helpers';
import { SectionDescription, SectionTitle } from 'pages/Teams/components/TeamInviteWizard/styles';
import { Invite, InvitesBatch, invitesReducer } from './invitesReducer';
import { UserTeamsPayload } from 'components/usersTable/types';
import {
  AlreadyInvitedEntry,
  alreadyInvitedReason,
  buildInvitesWithTeams,
  checkAlreadyInvited,
} from 'utils/invites';
import { LICENSE_REJECTION_OUTCOMES } from 'constants/errors/license_error_codes';
import { GENERIC_ERROR_MSG } from 'constants/errors/messages';
import { NOTIFICATION_TYPES } from 'constants/components/notification';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import useLicenseRejection from 'hooks/useLicenseRejection';

interface IAddEmails {
  onStepChange: (step: number) => void;
  /** Whether the invitees have teams, which widens the access step. */
  onTeamsChange?: (hasTeams: boolean) => void;
  onInvitesSubmit: (ok: boolean, invites?: Invite[]) => void;
  inviteAccOwner?: boolean;
  account?: AccountInvite;
}
const emailBodyTitle = 'Add email(s)';
const emailBodySubtitle = 'Add multiple emails separated by a comma (,).';

const licensesAvailablesLabel = 'Licenses available: ';

const teamRequiredMessage = 'Select a team you manage for every invited user.';

const TOTAL_STEPS = 4;
// The design shows three steps: emails, access, message. Fixing mistyped emails stays on the first.
const DESIGN_STEPS = 3;
const designStep = (step: number) => Math.max(step - 1, 0);
const SEND_INVITES_LABEL = 'Send Invites';

// Re-applies the team selection to an invitee, keeping its manager choice for the teams that are
// still selected.
const keepManagerChoices = (invite: Invite, selectedTeams: UserTeam[]): UserTeam[] =>
  selectedTeams.map((selected) => {
    const teamId = selected.team?.id || selected.team_id;
    const previous = invite.userTeams?.find(
      (userTeam) => (userTeam.team?.id || userTeam.team_id) === teamId
    );
    return previous ? { ...selected, is_manager: previous.is_manager } : selected;
  });

const AddEmails: React.FC<IAddEmails> = ({
  onStepChange,
  onTeamsChange,
  onInvitesSubmit,
  inviteAccOwner,
  account,
}) => {
  const [step, setStep] = useState(0); // we should pass some prop to launch specific step
  const [emails, setEmails] = useState<string[]>([]);
  const [errors, setErrors] = useState([]);
  const [inviteEditMobile, setinviteEditMobile] = useState(false);
  const [teams, setTeams] = useState<UserTeamsPayload[]>([]);
  const [userTeams, setUserTeams] = useState<UserTeam[]>([]);
  const [disabled, setDisabled] = useState(false);
  const {
    user: sessionUser,
    teams: sessionTeams,
    isTeamManager,
    isAccountOwner,
    isStaff,
    actions: { refreshAccounts, refreshSession },
  } = useContext(SessionContext);
  const notifyLicenseRejection = useLicenseRejection();
  const needsTeam =
    isTeamScopedManager({ isTeamManager, isAccountOwner, isStaff }) && !inviteAccOwner;
  const skillDiveFlagOn = useBooleanFlag(FEATURE_FLAGS.SKILL_DIVE_LICENSE_FLOW);
  // Account owner invites don't take Skill Dive.
  const skillDiveFlow = skillDiveFlagOn && !inviteAccOwner;
  const [invitesData, setInvitesData] = useReducer(invitesReducer, {
    message: null,
    account: inviteAccOwner ? account.id : undefined,
  } as InvitesBatch);
  const infiniteLicenses = account.licenses_available === true;
  const defaultMessage = defaultInviteMessage(
    sessionUser ? userFullName(sessionUser) : '',
    (userTeams.length === 1 && userTeams[0].name) || account?.name || ''
  );

  const accountDomains = account?.email_domains;

  const setupDefaultLicense = (email: string, newInvites) => {
    const exist = invitesData?.invites.find(({ email: inviteEmail }) => inviteEmail === email);
    if (exist) return exist.has_license;
    if (infiniteLicenses) return true;
    if (account.licenses_available > newInvites.filter(({ has_license }) => has_license).length)
      return true;
    return false;
  };

  const isAccountDomain = (email: string) => {
    const domain = getDomain(email);
    return accountDomains.some(({ email_domain }) => email_domain === domain);
  };

  const validateAreadyInvited = async (callback: { (alreadyInviteds): void }) => {
    const validEmails = invitesData.invites.map(({ email }) => email);
    setDisabled(true);
    if (inviteAccOwner) {
      callback(false);
    } else {
      try {
        const { rejected: invalidEmails, alreadyInvited } = await checkAlreadyInvited(validEmails);
        if (invalidEmails.length) {
          setErrors((prevState) => {
            const listed = prevState.filter((item) => item).map(({ email }) => email);
            return [...prevState, ...invalidEmails.filter(({ email }) => !listed.includes(email))];
          });
        }
        if (alreadyInvited.length) handleAlreadyInvited(alreadyInvited);
        callback(invalidEmails.length > 0 || alreadyInvited.length > 0);
      } catch (error) {
        Notification({ text: GENERIC_ERROR_MSG, type: NOTIFICATION_TYPES.ERROR });
      }
    }
    setDisabled(false);
  };

  const updateInvites = (newInvites: unknown[]) => {
    setInvitesData({
      type: 'SET_EMAIL_INVITES',
      value: newInvites,
    });
  };

  const sendInvites = async (invitesPayload: Invite[]) => {
    setDisabled(true);
    const inviteService = inviteAccOwner ? sendAccountOwnerInvites : sendUserInvites;
    try {
      const response = await inviteService({ ...invitesData, invites: invitesPayload });
      if (response?.status >= 200) {
        Notification({ text: 'Invitation sent successfully', type: 'success' });
        // The Skill Dive seats these invites reserved come off the counts shown elsewhere.
        if (invitesPayload.some(({ has_skill_dive }) => has_skill_dive)) {
          refreshSession();
          refreshAccounts();
        }
        onInvitesSubmit(true, invitesPayload);
      }
    } catch (error) {
      notifyLicenseRejection(error, {
        outcome: LICENSE_REJECTION_OUTCOMES.NO_INVITES_SENT,
        fallback: error?.response?.data?.detail || GENERIC_ERROR_MSG,
      });
    } finally {
      setDisabled(false);
    }
  };

  const handleNextPress = () => {
    const invitesWithLicenses = invitesData.invites.filter(({ has_license }) => has_license).length;
    const numericAvailable =
      typeof account.licenses_available === 'number'
        ? account.licenses_available
        : account.licenses_available
        ? Number.POSITIVE_INFINITY
        : 0;
    const insufficientLicenses = !infiniteLicenses && invitesWithLicenses > numericAvailable;
    if (step === 0 && emails.length > 0) {
      // On mobile, teams are picked per invitee on the access step.
      if (needsTeam && isDesktop && userTeams.length === 0) {
        Notification({ text: teamRequiredMessage, type: 'error' });
        return;
      }
      setUserTeamsToInvites();
      validateAreadyInvited((alreadyInviteds) => {
        alreadyInviteds || errors.length > 0 ? setStep(step + 1) : setStep(step + 2);
      });
    } else if (step === 2 && invitesData?.invites.length > 0 && !insufficientLicenses) {
      if (needsTeam && invitesData.invites.some((invite) => !invite.userTeams?.length)) {
        Notification({ text: teamRequiredMessage, type: 'error' });
        return;
      }
      setStep(3);
    } else if (step === 3) {
      // The payload drops `userTeams`, so it isn't written back: going back after a failed send
      // still needs it to render each invitee's teams.
      sendInvites(buildInvitesWithTeams(invitesData.invites, { withSkillDive: skillDiveFlow }));
    }
  };

  const handleAlreadyInvited = (alreadyInvited: unknown[]) => {
    const initialErrors = [];
    const invitationErrors = alreadyInvited.map((invitation: AlreadyInvitedEntry) => {
      if (
        !initialErrors.includes(invitation.email) &&
        !errors
          .filter((item) => item)
          .map(({ email }) => email)
          .includes(invitation.email)
      ) {
        initialErrors.push(invitation.email);
        return {
          email: invitation.email,
          error: alreadyInvitedReason(invitation, account.id),
        };
      }
    });
    setErrors((prevState) => [...prevState, ...invitationErrors.filter(Boolean)]);
  };

  const handleBackPress = () => {
    if (step === 0) {
      onInvitesSubmit(false);
    } else if (step === 2 && inviteEditMobile) {
      setinviteEditMobile(false);
    } else if (step === 2 && errors.length === 0) {
      setStep(0);
    } else {
      setStep(step <= 0 ? 0 : step - 1);
    }
  };

  const handleRemoveUsers = () => {
    const emailErrors = errors.map(({ email }) => email);
    const newEmails = emails.filter((email) => !emailErrors.includes(email));
    if (newEmails.length === 0) {
      setStep(0);
      setEmails(newEmails);
    } else {
      setEmails(newEmails);
      setErrors([]);
      setStep(step + 1);
    }
  };

  const handleRemoveInvitation = (email: string) => {
    const newEmails = emails.filter((inviteEmail) => inviteEmail !== email);
    setEmails(newEmails);
    if (invitesData.invites.length === 1) {
      updateInvites([]);
      setStep(0);
    }
  };

  const handleTeamChange = (newTeams: Team[]) => {
    if (isDesktop) {
      const updatedTeams = newTeams.reduce((acc, team) => {
        acc.push({ team_id: team.id, is_manager: false });
        return acc;
      }, []);
      const updatedUserTeams = newTeams.reduce((acc, team) => {
        acc.push({ team: team, is_manager: false });
        return acc;
      }, []);
      setTeams(updatedTeams);
      setUserTeams(updatedUserTeams);
      if (invitesData?.invites?.length) {
        const invitesUpdated = invitesData.invites.map((invite) => ({
          ...invite,
          userTeams: skillDiveFlow
            ? keepManagerChoices(invite, updatedUserTeams)
            : updatedUserTeams,
        }));
        updateInvites(invitesUpdated);
      }
    }
  };

  useEffect(() => {
    if (skillDiveFlagOn || !isTeamManager || sessionTeams?.length !== 1) return;
    if (userTeams.length > 0) return;
    const defaultTeam = sessionTeams[0];
    const updatedTeams = [{ team_id: defaultTeam.id, is_manager: false }];
    const updatedUserTeams = [{ team: defaultTeam, is_manager: false }];
    setTeams(updatedTeams);
    setUserTeams(updatedUserTeams);
    if (invitesData?.invites?.length) {
      const invitesUpdated = invitesData.invites.map((invite) => ({
        ...invite,
        userTeams: updatedUserTeams,
      }));
      updateInvites(invitesUpdated);
    }
  }, [invitesData?.invites, isTeamManager, sessionTeams, skillDiveFlagOn, userTeams.length]);

  const handleUpdateMessage = (message: string) => {
    setInvitesData({
      type: 'SET_MESSAGE',
      value: message || null,
    });
  };

  // The design opens the message step prefilled, and that text is sent unless it's changed.
  useEffect(() => {
    if (skillDiveFlow && step === 3 && invitesData.message === null) {
      handleUpdateMessage(defaultMessage);
    }
  }, [step]);

  const setUserTeamsToInvites = () => {
    if (userTeams.length > 0) {
      const invitesUpdated = [];
      invitesData.invites.map((i) => {
        // Going Back and then Next again keeps the manager choices made on the access step.
        i.userTeams = skillDiveFlow ? keepManagerChoices(i, userTeams) : userTeams;
        invitesUpdated.push(i);
      });
      updateInvites(invitesUpdated);
    }
  };

  useEffect(() => {
    onStepChange(step);
  }, [step]);

  useEffect(() => {
    onTeamsChange?.(teams.length > 0);
  }, [teams.length > 0]);

  useEffect(() => {
    let newInvites = [];
    let newErrors = [];
    emails.map((email) => {
      const inviteds = newInvites?.map(({ email }) => email) || [];
      const withErrors = newErrors?.map(({ email }) => email) || [];
      const existingInvite = invitesData.invites?.find((invite) => invite.email === email);
      newInvites =
        isEmail(email) && isAccountDomain(email) && !inviteds.includes(email)
          ? [
              ...newInvites,
              {
                email,
                is_manager: false,
                has_license: setupDefaultLicense(email, newInvites),
                is_account_owner: inviteAccOwner || false,
                userTeams: userTeams,
                teams: [],
                // Removing one invitee rebuilds the list; keep the others' Skill Dive and
                // per-team manager choices.
                ...(skillDiveFlow &&
                  existingInvite && {
                    has_skill_dive: existingInvite.has_skill_dive,
                    userTeams: existingInvite.userTeams || userTeams,
                  }),
              },
            ]
          : [...newInvites];
      newErrors =
        !isAccountDomain(email) && isEmail(email) && !withErrors.includes(email)
          ? [
              ...newErrors,
              {
                email: email,
                error: "Failed: The email address doesn't match with account valid domains",
              },
            ]
          : [...newErrors];
    });
    setErrors(newErrors);
    updateInvites(newInvites);
  }, [emails]);

  return (
    <>
      {isMobile && (
        <FeatherIcon
          icon={'arrow-left'}
          style={{ position: 'absolute', top: 20, left: 10 }}
          color={'#FFF'}
          onClick={handleBackPress}
        />
      )}
      {step === 0 && skillDiveFlow && (
        <AdByemailContainer>
          <SectionTitle>{SELECT_USERS_COPY.EMAILS_TITLE}</SectionTitle>
          <SectionDescription>{SELECT_USERS_COPY.EMAILS_HELPER}</SectionDescription>
          <InputMultiEmails emails={emails} setEmails={setEmails} />
          {isDesktop && (
            <div style={{ marginTop: 40 }}>
              <TeamSelect
                teamsSelected={userTeams}
                onTeamChange={handleTeamChange}
                pinOnlyManagedTeam={false}
                inviteDesign
                teamRequired={needsTeam}
              />
            </div>
          )}
        </AdByemailContainer>
      )}
      {step === 0 && !skillDiveFlow && (
        <AdByemailContainer>
          <BodyTitle>{emailBodyTitle}</BodyTitle>
          <EmailInput title={emailBodySubtitle} emails={emails} setEmails={setEmails} />
          <LicenseAvailables>
            <span>{licensesAvailablesLabel}</span>
            <b>{infiniteLicenses ? '∞' : account.licenses_available}</b>
          </LicenseAvailables>
          {isDesktop && (
            <TeamSelect
              teamsSelected={userTeams}
              onTeamChange={handleTeamChange}
              pinOnlyManagedTeam={!skillDiveFlagOn}
            />
          )}
        </AdByemailContainer>
      )}
      {step === 1 && errors.length > 0 && <RemoveUsers wrongEmails={errors} />}
      {step === 2 && (
        <AdjustUsersSettings
          hasTeam={Boolean(teams.length > 0)}
          invites={invitesData.invites}
          onInvitesUpdate={updateInvites}
          onInviteRemove={handleRemoveInvitation}
          onEdit={setinviteEditMobile}
          isEditing={inviteEditMobile}
          account={account}
          inviteAccOwner={inviteAccOwner}
        />
      )}
      {step === 3 &&
        (skillDiveFlow ? (
          <AddMessage
            onMessageUpdate={handleUpdateMessage}
            defaultValue={invitesData.message ?? defaultMessage}
          />
        ) : (
          <AddMessage onMessageUpdate={handleUpdateMessage} />
        ))}
      {!inviteEditMobile && (
        <Footer
          disabled={
            (!(invitesData?.invites?.length > 0) &&
              !(emails.filter((email) => isEmail(email)).length > 0)) ||
            (step === 0 && emails.some((email) => !isEmail(email))) ||
            disabled
          }
          hasBackButton={step !== 0}
          steps={skillDiveFlow ? DESIGN_STEPS : TOTAL_STEPS}
          topGap={skillDiveFlow && step === 2 ? 20 : undefined}
          stepActive={skillDiveFlow ? designStep(step) : step}
          onNextPress={step === 1 ? handleRemoveUsers : handleNextPress}
          onBackPress={handleBackPress}
          nextLabel={
            (step === 1 && 'Remove Users') ||
            (step === 3 && (skillDiveFlow ? SEND_INVITES_LABEL : 'Send invites')) ||
            'Next'
          }
        />
      )}
    </>
  );
};

export default AddEmails;
